import { createPortal } from 'react-dom';
import { useCreditPricing, formatCredits } from '../../hooks/useCreditPricing';
import { useCredits } from '../../hooks/useCredits';
import type { CostItem } from './CreditCostNotice';

interface Props {
  open: boolean;
  title?: string;
  items: CostItem[];
  balance?: number;
  note?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  // Quando definido, o modal vira "recorrente": mostra custo POR EXECUÇÃO + a cada quantas
  // horas roda + estimativa de gasto mensal, e não bloqueia por saldo (a automação corre no tempo).
  recurringHours?: number;
  // Escolhas que MUDAM o preço (ex.: motor de vídeo Standard × Ultra) e por isso precisam
  // ficar dentro do próprio card de cobrança — o usuário vê o total reagir ao que escolheu.
  children?: React.ReactNode;
}

/**
 * Modal de confirmação de custo — para ações de maior valor (≥ 0,50) ou fluxos
 * sem modal prévio. Mostra itens, total, saldo antes/depois e bloqueia se insuficiente.
 */
export function ConfirmCreditsModal({
  open, title, items, balance, note, confirmLabel, onConfirm, onCancel, recurringHours, children,
}: Props) {
  const { getMeta } = useCreditPricing();
  const { credits } = useCredits();

  if (!open) return null;

  const recurring = !!recurringHours && recurringHours > 0;
  const effectiveBalance = balance != null ? balance : (credits?.balance ?? 0);

  const lines = items
    .filter(it => it.qty > 0)
    .map(it => {
      const meta = getMeta(it.featureKey);
      const unitCost = it.unitCredits ?? meta?.cost ?? 0;
      const subtotal = Math.round(unitCost * it.qty * 10000) / 10000;
      return {
        subtotal,
        label: meta?.label || (it.unitCredits != null ? (it.label || 'Estimativa') : it.featureKey),
        detail: `${it.qty} ${it.label || meta?.unit || 'un.'} × ${formatCredits(unitCost)}`,
      };
    });

  const total = lines.reduce((sum, l) => sum + l.subtotal, 0);
  const after = Math.round((effectiveBalance - total) * 10000) / 10000;
  // Em modo recorrente não bloqueia por saldo (a automação roda ao longo do tempo).
  const insufficient = !recurring && after < 0;
  const runsPerMonth = recurring ? Math.max(1, Math.round((30 * 24) / (recurringHours as number))) : 0;
  const monthly = recurring ? Math.round(total * runsPerMonth * 100) / 100 : 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Confirmar consumo de créditos'}
      onClick={onCancel}
    >
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-lg">⚡</span>
          <h3 className="text-base font-black text-slate-900">{title || 'Confirmar consumo de créditos'}</h3>
        </div>

        {children && <div className="mb-3">{children}</div>}

        <div className="space-y-1.5 rounded-xl border border-slate-100 bg-slate-50 p-3">
          {lines.length === 0 && (
            <p className="text-xs text-slate-500">
              Nenhum item cobrável identificado para esta execução — nada deve ser debitado.
              {' '}Verifique se os itens desejados estão com a análise por IA ativada.
            </p>
          )}
          {lines.map((l, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">{l.label} <span className="text-slate-400">({l.detail})</span></span>
              <span className="font-mono font-semibold text-slate-800">{formatCredits(l.subtotal)}</span>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-1.5 text-sm">
            <span className="font-bold text-slate-800">{recurring ? 'Custo por execução' : 'Total'}</span>
            <span className="font-mono font-black text-slate-900">{formatCredits(total)} créditos</span>
          </div>
        </div>

        {recurring ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <p>⏱ Roda automaticamente <b>a cada {recurringHours}h</b> — cerca de <b>{runsPerMonth}×/mês</b> ≈ <b>{formatCredits(monthly)} créditos/mês</b> (estimativa).</p>
            <p className="mt-1">Se você ativar a análise por IA em <b>mais itens</b>, esse gasto aumenta automaticamente. Você pode desativar quando quiser.</p>
            <p className="mt-1 text-amber-700/70">Saldo atual: {formatCredits(effectiveBalance)} créditos.</p>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span>Saldo atual: <span className="font-semibold text-slate-700">{formatCredits(effectiveBalance)}</span></span>
            <span>
              Após: <span className={`font-semibold ${insufficient ? 'text-red-600' : 'text-emerald-600'}`}>{formatCredits(after)}</span>
            </span>
          </div>
        )}

        {note && <p className="mt-2 text-[11px] text-slate-400">{note}</p>}

        {insufficient && (
          <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">
            Saldo insuficiente para esta ação. Fale com o administrador para recarregar.
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={insufficient}
            className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {confirmLabel || (recurring ? 'Ativar' : 'Confirmar e executar')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
